import { Modal } from "../../utils/Modal";
import { Toast } from "../../utils/Toast";
import { t } from "../../utils/I18n";

/**
 * 공유하기 모달 — YouTube 식 공유 버튼.
 *
 * share-button 컴포넌트가 렌더한 [data-share-trigger] 클릭 시,
 * 같은 컴포넌트의 [data-share-template](서버 렌더 = 신뢰 HTML)을 복제해 Modal.content() 로 띄운다.
 * 모달 안에서 링크 복사(clipboard + execCommand 폴백) / 소셜 새 탭 공유를 처리한다.
 */
export class ShareModal {
  static init(): void {
    const triggers = document.querySelectorAll<HTMLElement>("[data-share-trigger]");
    triggers.forEach((trigger) => {
      trigger.addEventListener("click", () => ShareModal.open(trigger));
    });
  }

  private static open(trigger: HTMLElement): void {
    // 템플릿은 트리거의 다음 형제 또는 같은 컨테이너 안에서 탐색.
    const template = ShareModal.findTemplate(trigger);
    if (!template) return;

    const title = trigger.getAttribute("data-share-title") ?? "";
    const heading =
      ShareModal.label("posts.share", "Share") || "Share";

    // 신뢰 HTML(서버 렌더)을 모달에 주입.
    Modal.content(template.innerHTML, heading, "md").catch(() => {});

    // 모달 본문이 DOM 에 붙은 직후 바인딩(Modal.content 는 즉시 append).
    const modalBody = document.querySelector<HTMLElement>(
      ".app-modal-body .ui-share-modal",
    );
    if (!modalBody) return;
    ShareModal.bind(modalBody);
  }

  private static findTemplate(trigger: HTMLElement): HTMLTemplateElement | null {
    // 1) 다음 형제
    let el: Element | null = trigger.nextElementSibling;
    while (el) {
      if (el instanceof HTMLTemplateElement && el.hasAttribute("data-share-template")) {
        return el;
      }
      el = el.nextElementSibling;
    }
    // 2) 부모 컨테이너 내부
    const scoped = trigger
      .closest(".sec-article-header-split, body")
      ?.querySelector<HTMLTemplateElement>("[data-share-template]");
    return scoped ?? null;
  }

  private static bind(root: HTMLElement): void {
    const copyBtn = root.querySelector<HTMLElement>("[data-share-copy]");
    const urlField = root.querySelector<HTMLInputElement>("[data-share-url-field]");

    if (copyBtn && urlField) {
      copyBtn.addEventListener("click", () => {
        ShareModal.copy(urlField.value)
          .then((ok) => {
            if (ok) {
              Toast.success(ShareModal.label("posts.share_copied", "Link copied"));
            } else {
              Toast.error(ShareModal.label("posts.share_copy_failed", "Copy failed"));
            }
          })
          .catch(() =>
            Toast.error(ShareModal.label("posts.share_copy_failed", "Copy failed")),
          );
      });
    }

    // 소셜 링크는 기본 <a target="_blank"> 로 동작하므로 추가 JS 불필요.
  }

  /** clipboard API → execCommand 폴백(비-secure context 대응). */
  private static async copy(text: string): Promise<boolean> {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        /* 폴백으로 진행 */
      }
    }
    try {
      const input = document.createElement("textarea");
      input.value = text;
      input.setAttribute("readonly", "");
      input.style.position = "fixed";
      input.style.opacity = "0";
      document.body.appendChild(input);
      input.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(input);
      return ok;
    } catch {
      return false;
    }
  }

  /** I18n 키 → 번역, 미존재(키 그대로 반환)면 영어 폴백. */
  private static label(key: string, fallback: string): string {
    const translated = t(key);
    return translated === key ? fallback : translated;
  }
}
